<template>
  <div class="new-project-review">
    <div class="review-header line">
      <div class="project-name">
        <div class="name">{{ projectDetailsData.name }}</div>
        <div class="type">{{ $t(testType) }}</div>
      </div>
      <div class="project-price">
        <div class="total-price-title">{{ $t('newProject.review.total.price') }}</div>
        <div class="total-price-value">$ {{ projectProviderData.projectTotalPrice }}</div>
      </div>
    </div>
    <div class="review-details line">
      <div class="section-title">
        {{ $t('newProject.review.project.details') }}
      </div>
      <div class="section-content">
        <div class="manager">
          <div class="manager-contact-title sub-section-title">{{ $t('newProject.review.contact') }}</div>
          <div class="manager-contact-value">{{ managerName }}</div>
        </div>
        <div v-if="projectDetailsData.comment && projectDetailsData.comment.length" class="comment">
          <div class="comment-title sub-section-title">{{ $t('newProject.review.comment') }}</div>
          <div class="comment-value">{{ projectDetailsData.comment }}</div>
        </div>
      </div>
    </div>

    <div class="review-environment line">
      <div class="section-title">
        {{ $t('newProject.review.environment') }}
      </div>
      <div class="section-content">
        <div class="box-type">
          {{ $t(boxType) }}
        </div>
        <div class="sub-domains">
          <div class="sub-domains-content">
            <div
              class="sub-domains-content-line"
              v-for="(res, index) in projectResourcesData.resources"
              :key="res.value"
            >
              <div class="resource-header">
                <label class="resource-index"> {{ $t('newProject.resources.resource') }} #{{ index + 1 }}</label>
                <div class="resource-name">{{ res.value }}</div>
              </div>
              <div v-if="showCreds" class="resource-creds">
                <label class="resource-title creds-title"> {{ $t('newProject.resources.credentials') }}</label>
                <div class="column column-login-password">
                  {{ $t('newProject.resources.login') }}
                </div>
                <div class="column column-login-password">
                  {{ $t('newProject.resources.password') }}
                </div>
                <div class="creds-line" v-for="cred in res.credentials" :key="cred.name">
                  <div
                    class="sub-domain-username login column resource-data-text resource-data-text-login-password"
                    :title="cred.username"
                  >
                    {{ cred.username || '-' }}
                  </div>
                  <resource-password-row :key="cred.password" :password="cred.password" :copiable="false" />
                </div>
              </div>
              <div v-if="showFeatures(res)" class="resource-features">
                <label class="resource-title feats-title"> {{ $t('project.resources.features') }}</label>
                <div
                  class="column feature-block"
                  v-for="feature in res.features.filter(f => f.selected)"
                  :key="feature.value"
                >
                  <div class="resource-data-text feature-text" :title="$t(feature.value)">
                    {{ $pt('newProject.resources.feature', feature.value) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="review-bundles line">
      <div class="section-title">
        {{ $t('newProject.review.bundles') }}
      </div>
      <div class="section-content">
        <div class="bundle-type">
          {{ projectBundlesData.data }}
        </div>
      </div>
    </div>

    <div class="review-provider line">
      <div class="section-title">
        {{ $t('newProject.review.provider') }}
      </div>
      <div class="section-block">
        <div v-for="dat in datesData" :key="dat.title" class="section">
          <div class="sub-section-title">
            {{ $t(dat.title) }}
          </div>
          <div class="section-data">
            {{ dat.data }}
          </div>
        </div>
      </div>
      <div class="line">
        <div class="sp section-block">
          <div class="sub-section-title">
            {{ $t('newProject.review.security.provider') }}
          </div>
          <div class="security-provider-data" @click="toggleWorkerDetailsPopup">
            {{ projectProviderData.serviceProviderName }}
          </div>
        </div>
      </div>
    </div>

    <worker-details-popup
      :visible-popup="showWorkerDetailsPopup"
      :worker="workerDetails"
      @closed="toggleWorkerDetailsPopup"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatMonthDate } from '@/services/dates';
import { LOAD_WORKER_DETAILS } from '@/store/user/actionTypes';
import { testBoxTypes as TEST_BOX_TYPES, testTypes } from '@/constants/projects';

import WorkerDetailsPopup from '@/components/popup/WorkerDetailsPopup.vue';
import ResourcePasswordRow from '@/components/ProjectDetails/credentials/ResourcePasswordRow.vue';

export default {
  name: 'NewProjectReview',
  props: {
    newProject: Object,
  },
  components: {
    WorkerDetailsPopup,
    ResourcePasswordRow,
  },
  data() {
    return {
      showWorkerDetailsPopup: false,
      projectDetailsData: this.newProject.projectDetailsData,
      projectEnvironmentData: this.newProject.projectEnvironmentData,
      projectResourcesData: this.newProject.projectResourcesData,
      projectBundlesData: this.newProject.projectBundlesData,
      projectProviderData: this.newProject.projectProviderData,
      datesData: [
        {
          title: 'newProject.review.start.date',
          data: formatMonthDate(this.newProject.projectProviderData.startDate),
        },
        {
          title: 'newProject.review.end.date',
          data: formatMonthDate(this.newProject.projectProviderData.endDate),
        },
        {
          title: 'newProject.review.estimation',
          data: `${this.newProject.projectProviderData.projectDurationHours} ${this.$t(
            'newProject.review.working.hours',
          )}`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['profile', 'workerDetails']),
    boxType() {
      return this.newProject.projectEnvironmentData.configuration.data === TEST_BOX_TYPES.BLACK_BOX
        ? 'newProject.black.box'
        : 'newProject.gray.box';
    },
    showCreds() {
      return this.newProject.projectEnvironmentData.configuration.data === TEST_BOX_TYPES.GRAY_BOX;
    },
    testType() {
      return this.newProject.projectDetailsData.testType === testTypes.WEB_APP_TEST
        ? 'project.type.webapp'
        : 'project.type.infrastructure';
    },
    managerName() {
      const selectedManager = this.newProject.projectDetailsData.manager;
      return selectedManager && (selectedManager.name || `${selectedManager.firstName} ${selectedManager.lastName}`);
    },
    testBoxTypes() {
      return TEST_BOX_TYPES;
    },
  },
  watch: {
    newProject: {
      deep: true,
      handler() {
        this.$emit('setRequiredDataForStep', 5);
      },
    },
  },
  methods: {
    showFeatures(resource) {
      return resource.features.filter(f => f.selected).length;
    },
    async toggleWorkerDetailsPopup() {
      await this.$store.dispatch(LOAD_WORKER_DETAILS, {
        id: this.projectProviderData.serviceProviderId,
      });
      this.showWorkerDetailsPopup = !this.showWorkerDetailsPopup;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.new-project-review {
  height: inherit;
  overflow: auto;

  .line {
    padding-left: rem(32);
    padding-right: rem(32);
    display: block;
  }

  .review-header {
    margin-top: rem(40);
    height: rem(53);

    .project-name {
      float: left;
      display: inline-block;

      .name {
        height: rem(29);
        color: var(--text-primary);
        font-size: rem(24);
        font-weight: 600;
        letter-spacing: 0;
        line-height: rem(29);
      }

      .type {
        height: rem(20);
        color: var(--text-tertiary);
        font-size: $font-size-md;
        letter-spacing: 0;
        line-height: rem(20);
      }
    }

    .project-price {
      float: right;
      display: inline-block;

      .total-price-title {
        height: rem(10);
        color: var(--text-tertiary);
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: rem(10);
        text-transform: uppercase;
        text-align: right;
        margin-bottom: rem(5);
      }

      .total-price-value {
        height: rem(29);
        color: var(--text-secondary);
        font-size: rem(24);
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(29);
      }
    }
  }

  .section-title {
    height: rem(16);
    width: rem(164);
    color: var(--text-tertiary);
    font-size: $font-size-sm;
    font-weight: 600;
    letter-spacing: 0.96px;
    line-height: rem(16);
    text-transform: uppercase;
    vertical-align: top;
    display: inline-block;
  }

  .section-block {
    display: inline-block;
  }

  .section-content {
    width: rem(593);
    display: inline-block;
  }

  .review-details {
    margin-top: rem(40);
    max-height: rem(220);

    .manager {
      .manager-contact-value {
        color: var(--text-primary);
        font-size: $font-size-base;
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(16);
      }
    }
    .comment {
      .comment-title {
        margin-top: rem(19);
      }
      .comment-value {
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: rem(18);
        max-height: rem(151);
        overflow: auto;
      }
    }
  }

  .sub-section-title {
    height: rem(10);
    color: var(--text-tertiary);
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-2;
  }

  .review-environment {
    margin-top: rem(39);

    .box-type {
      height: rem(20);
      color: var(--text-primary);
      font-size: $font-size-md;
      font-weight: bold;
      letter-spacing: 0;
      line-height: rem(20);
      margin-bottom: $space-2;
    }

    .sub-domains {
      box-sizing: border-box;
      width: rem(592);
      background-color: var(--bg-primary);
      overflow: hidden;
      margin-top: rem(14);

      .sub-domains-content {
        overflow: auto;

        .resource-data-text {
          color: var(--text-primary);
          font-size: $font-size-base;
          letter-spacing: 0;
          line-height: rem(16);

          &.resource-data-text-login-password {
            padding-left: $space-1;
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .sub-domains-content-line {
          min-height: rem(32);
          margin-top: rem(11);
          border: 1px solid rgba(159, 165, 180, 0.5);
          border-radius: $radius-md;
        }

        .resource-header {
          padding-top: $space-1;
          padding-bottom: rem(11);
          border-bottom: solid 1px rgba(159, 165, 180, 0.5);
        }

        .resource-creds {
          padding-top: $space-2;
          padding-bottom: $space-2;
          border-bottom: solid 1px rgba(159, 165, 180, 0.5);
        }

        .resource-features {
          padding-top: $space-2;
          padding-bottom: $space-2;
        }

        .feature-block {
          vertical-align: top;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .feature-text {
          margin-bottom: $space-2;
          padding-left: $space-4;
        }

        .resource-name {
          color: var(--text-primary);
          font-size: $font-size-base;
          font-weight: bold;
          letter-spacing: 0;
          line-height: rem(16);
          padding-left: $space-4;
        }

        .resource-title {
          display: block;
          color: var(--text-tertiary);
          font-size: $font-size-base;
          letter-spacing: 0;
          line-height: rem(16);
          text-transform: capitalize;
          padding-left: $space-4;

          &.creds-title {
            margin-bottom: rem(5);
          }
          &.feats-title {
            margin-bottom: rem(15);
          }
        }

        .margin-bottom-a-lot {
          margin-bottom: rem(15);
        }

        .resource-index {
          padding-left: $space-4;
          color: var(--text-tertiary);
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: rem(10);
          text-transform: uppercase;
        }

        .creds-line {
          display: flex;
          border-radius: $radius-sm;
          background-color: var(--bg-secondary);
          margin-bottom: rem(1);
          margin-left: $space-2;
          margin-right: $space-4;
          height: rem(28);
          padding-top: $space-1;

          .resource-password-row {
            justify-content: space-between;
            flex: 1;
          }
        }

        .column {
          display: inline-block;
          width: rem(195);

          &.column-login-password {
            padding-left: $space-4;
            color: var(--text-tertiary);
            font-size: $font-size-xs;
            letter-spacing: 0.8px;
            line-height: rem(10);
            text-transform: uppercase;
          }
        }
      }

      .sub-domain {
        width: rem(212);
        display: inline-block;
      }
      .creds-block {
        display: inline-block;
      }
      .sub-domain-username {
        width: rem(152);
        display: inline-block;
      }
      .sub-domain-password {
        width: rem(185);
        display: inline-block;
      }
      .resources-header {
        div {
          height: rem(10);
          color: var(--text-tertiary);
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: rem(10);
          display: inline-block;
          text-transform: uppercase;
          margin-top: rem(15);
        }
      }
    }
  }

  .review-bundles {
    margin-top: rem(39);

    .bundle-type {
      height: rem(20);
      color: var(--text-primary);
      font-size: $font-size-md;
      font-weight: bold;
      letter-spacing: 0;
      line-height: rem(20);
      margin-bottom: $space-2;
    }
  }

  .review-provider {
    margin-top: rem(39);

    .section {
      margin-right: rem(90);
      display: inline-block;
    }

    .section-data {
      height: rem(20);
      color: var(--text-primary);
      font-size: $font-size-md;
      font-weight: bold;
      letter-spacing: 0;
      line-height: rem(20);
      margin-bottom: $space-2;
    }

    .sp {
      margin-top: rem(19);
      margin-left: rem(132);
      margin-bottom: $space-2;

      .security-provider-data {
        height: rem(16);
        color: var(--text-secondary);
        font-size: $font-size-base;
        font-weight: 500;
        letter-spacing: 0;
        line-height: rem(16);
        cursor: pointer;
      }
    }
  }
}
</style>
